import React,{useState} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import useDashboardStore from '../store/dashboardStore';

const {FiX,FiAlertTriangle,FiTrash2,FiWifi,FiWifiOff}=FiIcons;

const ClearDatabaseModal=()=> {
  const {clearModalOpen,toggleClearModal,clearDatabase,loading,error,testConnection}=useDashboardStore();
  const [confirmText,setConfirmText]=useState('');
  const [isClearing,setIsClearing]=useState(false);
  const [connectionStatus,setConnectionStatus]=useState(null);

  const isConfirmed=confirmText.trim()==='DELETE';

  // Test connection when modal opens
  React.useEffect(() => {
    if (clearModalOpen) {
      testConnectionStatus();
    }
  }, [clearModalOpen]);

  const testConnectionStatus = async () => {
    setConnectionStatus('testing');
    const isConnected = await testConnection();
    setConnectionStatus(isConnected ? 'connected' : 'failed');
  };

  const handleClear=async ()=> {
    if (!isConfirmed || isClearing) return;

    setIsClearing(true);
    try {
      await clearDatabase();
      setConfirmText('');
      // Modal will be closed by the store after successful clear
    } catch (error) {
      console.error('Error clearing database:',error);
    } finally {
      setIsClearing(false);
    }
  };

  const handleClose=()=> {
    if (isClearing) return;
    setConfirmText('');
    setConnectionStatus(null);
    toggleClearModal();
  };

  return (
    <AnimatePresence>
      {clearModalOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          onClick={handleClose}
        >
          <motion.div
            className="bg-brand-card rounded-card p-6 w-full max-w-md mx-4 shadow-card border border-brand-bad/20"
            initial={{scale: 0.9,opacity: 0}}
            animate={{scale: 1,opacity: 1}}
            exit={{scale: 0.9,opacity: 0}}
            onClick={(e)=> e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-brand-bad/20 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-brand-bad" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-brand-fg">Clear Database</h3>
                  <p className="text-sm text-brand-muted">This action cannot be undone</p>
                </div>
              </div>
              {!isClearing && (
                <motion.button
                  onClick={handleClose}
                  className="p-2 hover:bg-brand-muted2/20 rounded-button transition-colors"
                  whileHover={{scale: 1.05}}
                  whileTap={{scale: 0.95}}
                >
                  <SafeIcon icon={FiX} className="w-5 h-5 text-brand-muted" />
                </motion.button>
              )}
            </div>

            <div className="mb-6">
              {/* Connection Status */}
              <div className="mb-4 p-3 rounded-lg border border-brand-muted2/20 bg-brand-muted2/10">
                <div className="flex items-center space-x-2 mb-2">
                  {connectionStatus === 'testing' && (
                    <>
                      <div className="w-4 h-4 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" />
                      <span className="text-brand-fg text-sm">Testing connection...</span>
                    </>
                  )}
                  {connectionStatus === 'connected' && (
                    <>
                      <SafeIcon icon={FiWifi} className="w-4 h-4 text-brand-good" />
                      <span className="text-brand-good text-sm">Database connected</span>
                    </>
                  )}
                  {connectionStatus === 'failed' && (
                    <>
                      <SafeIcon icon={FiWifiOff} className="w-4 h-4 text-brand-bad" />
                      <span className="text-brand-bad text-sm">Connection failed</span>
                    </>
                  )}
                </div>
                {connectionStatus === 'failed' && (
                  <div className="text-xs text-brand-muted">
                    <p>Please check your internet connection and try again.</p>
                    <motion.button
                      onClick={testConnectionStatus}
                      className="mt-2 text-brand-accent hover:text-brand-accent/80 underline"
                      whileHover={{scale: 1.02}}
                    >
                      Retry connection test
                    </motion.button>
                  </div>
                )}
              </div>

              <div className="bg-brand-bad/10 border border-brand-bad/20 rounded-lg p-4 mb-4">
                <p className="text-brand-bad text-sm font-medium mb-2">⚠️ Warning</p>
                <p className="text-brand-fg text-sm">
                  This will permanently delete all sales data from the database. All uploaded records,
                  transactions, and historical data will be lost.
                </p>
              </div>

              {/* Show error if any */}
              {error && (
                <motion.div
                  className="mb-4 p-4 bg-brand-bad/10 border border-brand-bad/20 rounded-lg"
                  initial={{opacity: 0,y: -10}}
                  animate={{opacity: 1,y: 0}}
                >
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-brand-bad" />
                    <span className="text-brand-bad font-medium">Error</span>
                  </div>
                  <p className="text-brand-bad text-sm mt-1">{error}</p>
                  
                  {/* Troubleshooting tips */}
                  <div className="mt-3 p-3 bg-brand-bad/5 rounded border border-brand-bad/10">
                    <p className="text-brand-bad text-xs font-medium mb-2">💡 Troubleshooting Tips:</p>
                    <ul className="text-brand-bad text-xs space-y-1">
                      <li>• Check your internet connection</li>
                      <li>• Refresh the page and try again</li>
                      <li>• Close other browser tabs to free up memory</li>
                      <li>• Try again in a few minutes if the server is busy</li>
                    </ul>
                  </div>
                </motion.div>
              )}

              <div>
                <label className="block text-brand-fg text-sm font-medium mb-2">
                  Type <span className="text-brand-bad font-bold">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e)=> setConfirmText(e.target.value)}
                  disabled={isClearing || connectionStatus === 'failed'}
                  className="w-full bg-brand-muted2/10 text-brand-fg rounded-button px-4 py-3 border border-brand-muted2/20 focus:border-brand-bad focus:outline-none transition-colors disabled:opacity-50"
                  placeholder="Type DELETE here..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <motion.button
                onClick={handleClose}
                disabled={isClearing}
                className="px-4 py-2 bg-brand-muted2/20 hover:bg-brand-muted2/30 text-brand-fg rounded-button transition-colors disabled:opacity-50"
                whileHover={!isClearing ? {scale: 1.02} : {}}
                whileTap={!isClearing ? {scale: 0.98} : {}}
              >
                Cancel
              </motion.button>
              
              <motion.button
                onClick={handleClear}
                disabled={!isConfirmed || isClearing || connectionStatus === 'failed'}
                className={`flex items-center space-x-2 px-4 py-2 rounded-button transition-colors ${
                  isConfirmed && !isClearing && connectionStatus !== 'failed'
                    ? 'bg-brand-bad hover:bg-brand-bad/80 text-white'
                    : 'bg-brand-muted2/20 text-brand-muted2 cursor-not-allowed'
                }`}
                whileHover={isConfirmed && !isClearing && connectionStatus !== 'failed' ? {scale: 1.02} : {}}
                whileTap={isConfirmed && !isClearing && connectionStatus !== 'failed' ? {scale: 0.98} : {}}
              >
                {isClearing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Clearing...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    <span>Clear Database</span>
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ClearDatabaseModal;